/**
 * useContractPubSub - live extraction updates for ONE contract.
 *
 * Connects to the Azure Web PubSub "ai_processing" hub, joins the contract's
 * group (the bare contract uuid - same convention as tables/reviews), and calls
 * `onEvent` when the engine emits an extraction event. This fully replaces the
 * old poll-every-8s loop: when extraction finishes, the engine emits
 * `contract.extraction_completed` to the group and the detail/dashboard refetch.
 *
 * Events handled (emitted by pons-ai-engine/app/handlers/contract_extractor.py):
 *   contract.extraction_started    → status 'processing'
 *   contract.extraction_completed  → refetch (facts + intelligence now present)
 *   contract.extraction_failed     → refetch (status flips to 'failed')
 */

import { useEffect, useRef, useCallback } from 'react';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';

const HUB = 'ai_processing';

export interface ContractExtractionEvent {
  event: 'started' | 'completed' | 'failed';
  contract_uuid?: string;
  extraction_status?: string;
  contract_status?: string;
  risk_level?: string | null;
}

/**
 * Subscribe to live extraction events for a single contract.
 *
 * @param contractUuid  The contract UUID (also the PubSub group).
 * @param enabled       Only connect while it matters (e.g. extraction pending).
 * @param onEvent       Called on each extraction event (started/completed/failed).
 * @param userId        Optional - seeds the socket's user id so negotiate works
 *                      even if nothing else seeded it yet.
 */
export function useContractPubSub(
  contractUuid: string | undefined,
  enabled: boolean,
  onEvent: (evt: ContractExtractionEvent) => void,
  userId?: string | number | null,
) {
  const cbRef = useRef(onEvent);
  useEffect(() => { cbRef.current = onEvent; }, [onEvent]);

  // MultiHubSocketService wildcard ('*') callback signature is
  // (…eventArgs, eventName): data FIRST, event name LAST.
  const handleEvent = useCallback((data: any, eventName: string) => {
    if (!data || typeof data !== 'object') return;
    const payload = data.data || data; // unwrap the PubSub envelope
    if (eventName === 'contract.extraction_started') {
      cbRef.current({ event: 'started', ...payload });
    } else if (eventName === 'contract.extraction_completed') {
      cbRef.current({ event: 'completed', ...payload });
    } else if (eventName === 'contract.extraction_failed') {
      cbRef.current({ event: 'failed', ...payload });
    }
  }, []);

  useEffect(() => {
    if (!contractUuid || !enabled) return;
    let cancelled = false;
    const service = getMultiHubSocketService();
    const handler = (data: any, eventName: string) => handleEvent(data, eventName);

    (async () => {
      try {
        if (userId != null) service.setCurrentUserId(String(userId));
        const connected = await service.connectToHub(HUB);
        if (cancelled || !connected) return;
        service.emit(HUB, 'join', { group: contractUuid });
        service.on(HUB, '*', handler);
      } catch {
        /* live updates are a nicety - never throw into the view */
      }
    })();

    return () => {
      cancelled = true;
      const svc = getMultiHubSocketService();
      try {
        svc.emit(HUB, 'leave', { group: contractUuid });
        svc.off(HUB, '*', handler);
      } catch {
        /* ignore teardown races */
      }
    };
  }, [contractUuid, enabled, userId, handleEvent]);
}

export default useContractPubSub;
